// src/components/ui/dialog.tsx
'use client';

import * as React from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { X } from 'lucide-react';

export const Dialog = DialogPrimitive.Root;
export const DialogTrigger = DialogPrimitive.Trigger;
export const DialogPortal = DialogPrimitive.Portal;

export function DialogOverlay(props: React.ComponentProps<typeof DialogPrimitive.Overlay>) {
  return (
    <DialogPrimitive.Overlay
      {...props}
      className="fixed inset-0 z-50 bg-black/50 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0"
    />
  );
}

export function DialogContent(
  { className = '', children, ...props }: React.ComponentProps<typeof DialogPrimitive.Content>
) {
  // Ensure a11y warning is silenced when no description is provided.
  // Radix recommends setting aria-describedby={undefined} if you intentionally
  // omit a <Description/>. Respect any explicit aria-describedby passed in.
  const { ['aria-describedby']: ariaDescribedBy, ...rest } = props as any;

  return (
    <DialogPortal>
      <DialogOverlay />
      <DialogPrimitive.Content
        aria-describedby={ariaDescribedBy ?? undefined}
        {...rest}
        className={
          `fixed left-1/2 top-1/2 z-50 grid w-full max-w-lg -translate-x-1/2 -translate-y-1/2 gap-4 border 
           bg-white dark:bg-zinc-900 p-6 shadow-lg duration-200 
           data-[state=open]:animate-in data-[state=closed]:animate-out 
           data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 
           data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 
           data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] 
           data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] rounded-lg ${className}`
        }
      >
        {children}
        <DialogPrimitive.Close className="absolute right-4 top-4 rounded-sm opacity-70 transition-opacity hover:opacity-100 focus:outline-none">
          <X className="h-5 w-5" />
          <span className="sr-only">Close</span>
        </DialogPrimitive.Close>
      </DialogPrimitive.Content>
    </DialogPortal>
  );
}

// Optional export for consumers who want to provide a description
export const DialogDescription = DialogPrimitive.Description;

export function DialogHeader(props: React.HTMLAttributes<HTMLDivElement>) {
  const { className = '', ...rest } = props;
  return <div {...rest} className={`flex flex-col space-y-1.5 text-left ${className}`} />;
}

export function DialogTitle(props: React.ComponentProps<typeof DialogPrimitive.Title>) {
  const { className = '', ...rest } = props;
  return <DialogPrimitive.Title {...rest} className={`text-lg font-semibold leading-none tracking-tight ${className}`} />;
}

export function DialogFooter(props: React.HTMLAttributes<HTMLDivElement>) {
  const { className = '', ...rest } = props;
  return <div {...rest} className={`flex items-center justify-end gap-2 ${className}`} />;
}
